import classNames from "classnames";
import { ItemSelectionKind, SelectableItemOptions } from "client/Hooks";
import { Coordinate, Rect } from "client/Misc/commonTypes";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { doOverlap, LassoContainerContext } from "../LassoContainer/LassoContainer";
import { SelectCell } from "./Cell";
import {
	RootContext,
	BodyRowIndexContext,
	RowCellIndexContext,
	RowContext,
	RowContextData,
	RowVariant
} from "./contexts";
import {
	rowHeight,
	headerHeight,
	getSelectionKindFromClickEvent
} from "./misc";
import styles from "./TableView.module.less";

export type TableViewRowProps = {
	selectable?: SelectableItemOptions;
	onExecute?(): void;
	onContextMenu?(coordinate: Coordinate): void;
	isDisabled?: boolean;
	trackingId: string;
	onIsHoverChange?(isHover: boolean): void;
};

export const Row = (props: PropsWithChildren<TableViewRowProps>): ReactElement => {
	const {
		trackingId,
		isDisabled = false,
		onIsHoverChange,
		children
	} = props;

	let {
		selectable = false,
		onExecute,
		onContextMenu,
	} = props;

	if (isDisabled) {
		selectable = false;
		onExecute = undefined;
		onContextMenu = undefined;
	}

	const [hasHover, setHasHover] = useState<boolean>(false);

	const childrenArray = useMemo(
		(): ReactNode[] => flattenChildren(children),
		[children]
	);

	const {
		doesSupportMultiSelect,
		contentWidth,
		hasZebraPrinting,
		shouldHideDisabledCheckboxes
	} = useContext(RootContext);

	const {
		hasLassoContainer,
		lassoRect
	} = useContext(LassoContainerContext);

	const hasLassoRect: boolean = hasLassoContainer && Boolean(lassoRect);
	const rowIndex: number = useContext(BodyRowIndexContext);

	const hasInteraction: boolean = Boolean(selectable)
		|| isNotNil(onExecute)
		|| isNotNil(onContextMenu);

	const hoverChangeHandler = useCallback(
		(isHovered: boolean) => {
			setHasHover(isHovered);
			if (onIsHoverChange) {
				onIsHoverChange(isHovered);
			}
		},
		[onIsHoverChange]
	);

	const rowVariant: RowVariant = !hasZebraPrinting
		? "default"
		: rowIndex % 2 === 0
			? "even"
			: "odd";

	const contextData = useMemo(
		(): RowContextData => ({
			hasHover: hasInteraction && hasHover,
			selectable,
			onExecute,
			isDisabled,
			shouldHideDisabledCheckboxes
		}),
		[
			hasHover,
			hasInteraction,
			selectable,
			onExecute,
			isDisabled,
			shouldHideDisabledCheckboxes
		]
	);

	const doesOverlapWithLasso = useMemo(
		(): boolean => {
			if (!hasLassoContainer || !lassoRect) {
				return false;
			}

			const left: number = 0;
			const top: number = headerHeight + rowHeight * rowIndex;

			const itemRect: Rect = {
				left,
				top,
				right: left + contentWidth,
				bottom: top + rowHeight
			};

			return doOverlap(itemRect, lassoRect);
		},
		[
			contentWidth,
			hasLassoContainer,
			rowIndex,
			lassoRect
		]
	);

	useEffect(
		(): void => {
			if (!selectable || !hasLassoRect) {
				return;
			}

			return selectable.onSelect(
				doesOverlapWithLasso
					? ItemSelectionKind.AddToScope
					: ItemSelectionKind.RemoveFromScope
			);
		},
		[
			doesOverlapWithLasso,
			selectable,
			hasLassoRect
		]
	);

	const mouseEnterHandler = useCallback(
		(): void => {
			hoverChangeHandler(true);
		},
		[hoverChangeHandler]
	);

	const mouseLeaveHandler = useCallback(
		(): void => {
			hoverChangeHandler(false);
		},
		[hoverChangeHandler]
	);

	const selectHandler = useCallback(
		(event: MouseEvent): void => {
			if (selectable) {
				selectable.onSelect(
					getSelectionKindFromClickEvent(event)
				);
			}
		},
		[selectable]
	);

	const contextMenuHandler = useCallback(
		(event: MouseEvent): void => {
			if (selectable && !selectable.isSelected) {
				selectHandler(event);
			}

			if (!onContextMenu) {
				return;
			}

			event.preventDefault();
			onContextMenu({
				x: event.clientX,
				y: event.clientY
			});
		},
		[
			selectable,
			onContextMenu,
			selectHandler
		]
	);

	return (
		<div
			{...setTrackingId("row", `|${trackingId}`)}
			className={classNames(
				styles.row,
				{
					[styles.row_hovered]: hasInteraction && hasHover,
					[styles.row_selected]: selectable && selectable.isSelected,
					[styles.row_zebraEven]: rowVariant === "even",
				}
			)}
			onMouseEnter={mouseEnterHandler}
			onMouseLeave={mouseLeaveHandler}
			onContextMenu={contextMenuHandler}
			onClick={
				selectable
					? selectHandler
					: undefined
			}
		>
			<RowContext.Provider value={contextData}>
				{doesSupportMultiSelect && (
					<SelectCell />
				)}
				{childrenArray.map(
					(child, index) => (
						<RowCellIndexContext.Provider
							key={getReactNodeKey(child, index)}
							value={index}
						>
							{child}
						</RowCellIndexContext.Provider>
					)
				)}
			</RowContext.Provider>
		</div>
	);
};
Row.displayName = "TableView.Row";
